// Role hierarchy from least to most privileged (use role keys for compatibility, but prefer role objects)
export const ROLE_HIERARCHY = ['student', 'teacher', 'designer', 'manager', 'admin', 'superadmin'];

/**
 * Checks if a user has a specific role (by role key or role_id)
 * @param {Array} roles - Array of role objects, each with at least { id, role }
 * @param {string} roleKeyOrId - Role key (string) or role_id (UUID)
 * @returns {boolean} True if user has the role
 */
export const hasRole = (roles, roleKeyOrId) => {
  if (!Array.isArray(roles) || !roleKeyOrId) return false;
  return roles.some(r => r.role === roleKeyOrId || r.id === roleKeyOrId);
};

/**
 * Checks if the current user has any of the specified roles (by role key or role_id)
 * @param {Array} roles - Array of role objects
 * @param {Array<string>} roleKeysOrIds - Array of role keys or role_ids
 * @returns {boolean} True if user has any of the specified roles
 */
export const hasAnyRole = (roles, roleKeysOrIds) => {
  if (!Array.isArray(roles) || !Array.isArray(roleKeysOrIds)) return false;
  return roles.some(r => roleKeysOrIds.includes(r.role) || roleKeysOrIds.includes(r.id));
};

/**
 * Checks if the user has at least the specified minimum role in the hierarchy
 * @param {Array} roles - Array of role objects
 * @param {string} minRoleKey - Minimum role key (e.g., 'teacher')
 * @returns {boolean} True if user has a role >= minRoleKey
 */
export const hasMinRole = (roles, minRoleKey) => {
  if (!Array.isArray(roles) || !minRoleKey) return false;
  // Find the highest role in the user's roles
  const highestIndex = Math.max(...roles.map(r => ROLE_HIERARCHY.indexOf(r.role)).filter(i => i >= 0));
  const minIndex = ROLE_HIERARCHY.indexOf(minRoleKey);
  return highestIndex >= minIndex;
};

// Semantic helpers
export const isStudent = (roles) => hasRole(roles, 'student');
export const isTeacher = (roles) => hasMinRole(roles, 'teacher');
export const isDesigner = (roles) => hasMinRole(roles, 'designer');
export const isManager = (roles) => hasMinRole(roles, 'manager');
export const isAdmin = (roles) => hasRole(roles, 'admin') || hasRole(roles, 'superadmin');
export const isSuperAdmin = (roles) => hasRole(roles, 'superadmin');

/**
 * Gets the highest role key for a user
 * @param {Array} roles - Array of role objects
 * @returns {string|null} The highest role key or null
 */
export const getUserHighestRole = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  let highest = roles[0].role;
  let highestIndex = ROLE_HIERARCHY.indexOf(highest);
  for (const r of roles) {
    const idx = ROLE_HIERARCHY.indexOf(r.role);
    if (idx > highestIndex) {
      highest = r.role;
      highestIndex = idx;
    }
  }
  return highest;
};

/**
 * Gets the display label for a role (from backend or fallback)
 * @param {Object|string} roleObjOrKey - Role object or role key
 * @returns {string}
 */
export const getRoleLabel = (roleObjOrKey) => {
  if (!roleObjOrKey) return '';
  if (typeof roleObjOrKey === 'object' && roleObjOrKey.label) return roleObjOrKey.label;
  const key = typeof roleObjOrKey === 'object' ? roleObjOrKey.role : roleObjOrKey;
  const roleData = AVAILABLE_ROLES.find(r => r.key === key);
  return roleData ? roleData.label : key;
};

/**
 * Gets the description for a role (from backend or fallback)
 * @param {Object|string} roleObjOrKey - Role object or role key
 * @returns {string}
 */
export const getRoleDescription = (roleObjOrKey) => {
  if (!roleObjOrKey) return '';
  if (typeof roleObjOrKey === 'object' && roleObjOrKey.description) return roleObjOrKey.description;
  const key = typeof roleObjOrKey === 'object' ? roleObjOrKey.role : roleObjOrKey;
  const roleData = AVAILABLE_ROLES.find(r => r.key === key);
  return roleData ? roleData.description : '';
};

// AVAILABLE_ROLES is now only for fallback and display. Prefer backend-provided metadata.
export const AVAILABLE_ROLES = [
  { key: 'student', label: 'Student', description: 'Access student dashboard' },
  { key: 'teacher', label: 'Teacher', description: 'Access teacher dashboard' },
  { key: 'designer', label: 'Course Designer', description: 'Access course designer dashboard' },
  { key: 'manager', label: 'Course Manager', description: 'Access course manager dashboard' },
  { key: 'admin', label: 'Admin', description: 'Access admin dashboard' },
  { key: 'superadmin', label: 'Super Admin', description: 'Access all system features' },
];

/**
 * Checks if a user has a specific role
 * @param {Object} user - The user object from useAuth
 * @param {string} role - The role to check
 * @returns {boolean} True if user has the role
 */

